// recovery-codes-modal.js — recovery-codes display modal.
//
// Shown right after MFA enrollment succeeds and when the user
// clicks "Regenerate codes" from the 2FA panel. The codes are
// shown ONCE — Supabase only returns them at generation time;
// users who close this modal without saving lose them.
//
// Safety UX: the "I've saved them" button is disabled until the
// user has either Downloaded the codes as .txt or Copied them
// to the clipboard. The Escape key explicitly does NOT close
// this modal (handled in bootstrap.js's keyboard handler) —
// losing the codes mid-display is catastrophic.
//
// Hoisted from bootstrap.js on 2026-05-13 (Phase K-e). Pure
// DOM ops over static index.html scaffolding (#recovery-codes-*).
import { $ } from "./dom.js";
import { escapeHTML } from "./util.js";

// showRecoveryCodesModal(codes): show the modal with `codes`,
// resolves when the user clicks "Done" (only enabled after a
// successful Download or Copy). Listeners are torn down on
// cleanup so a re-open can't stack them.
export function showRecoveryCodesModal(codes) {
  return new Promise((resolve) => {
    const bd = $("recovery-codes-backdrop");
    const grid = $("recovery-codes-grid");
    const status = $("recovery-codes-status");
    const dl = $("recovery-codes-download");
    const cp = $("recovery-codes-copy");
    const done = $("recovery-codes-done");
    let saved = false;
    grid.innerHTML = codes
      .map(
        (c) =>
          '<code style="padding: 8px 10px; background: var(--bg); border: 1px solid var(--border); border-radius: var(--r-sm); font-size: 13px; color: var(--accent); text-align: center; letter-spacing: 0.05em;">' +
          escapeHTML(c) +
          "</code>",
      )
      .join("");
    status.textContent =
      "Download or copy these first — then you can dismiss.";
    done.disabled = true;
    dl.disabled = false;
    cp.disabled = false;
    bd.classList.add("open");

    const markSaved = () => {
      saved = true;
      done.disabled = false;
      status.innerHTML =
        '<span style="color: var(--success);">✓ Saved. You can dismiss when ready.</span>';
    };

    const onDownload = () => {
      try {
        const d = new Date();
        const fname =
          "agentcollision-recovery-codes-" + d.toISOString().slice(0, 10) + ".txt";
        const content =
          "AgentCollision recovery codes\n" +
          "Generated: " +
          d.toISOString() +
          "\n\n" +
          "Each code works once. Keep this file somewhere safe.\n\n" +
          codes.join("\n") +
          "\n";
        const blob = new Blob([content], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fname;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        markSaved();
      } catch (e) {
        status.innerHTML =
          '<span style="color: var(--danger);">Download failed: ' +
          escapeHTML(e.message || "unknown") +
          "</span>";
      }
    };
    const onCopy = async () => {
      try {
        await navigator.clipboard.writeText(codes.join("\n"));
        markSaved();
      } catch (e) {
        status.innerHTML =
          '<span style="color: var(--danger);">Copy failed. Use Download instead.</span>';
      }
    };
    const cleanup = () => {
      bd.classList.remove("open");
      dl.removeEventListener("click", onDownload);
      cp.removeEventListener("click", onCopy);
      done.removeEventListener("click", onDone);
    };
    const onDone = () => {
      if (!saved) return;
      cleanup();
      resolve();
    };
    dl.addEventListener("click", onDownload);
    cp.addEventListener("click", onCopy);
    done.addEventListener("click", onDone);
  });
}
